import { useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import {
  addDays,
  addMonths,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isBefore,
  isAfter,
  isValid,
  isWithinInterval,
  parseISO,
  startOfMonth,
  startOfDay,
  startOfWeek,
  subMonths
} from "date-fns";
import {
  CalendarCheck2,
  CalendarDays,
  ChevronRight,
  List,
  Loader2,
  Search,
  X
} from "lucide-react";
import { MonthNav } from "@/features/grc/components/MonthNav";
import { MonthYearPicker } from "@/features/grc/components/MonthYearPicker";
import {
  useAttendeeLevels,
  useCalendarEvents,
  useCalendarHolidays,
  useDraftBusinesses,
  useDraftCalendar,
  useDraftCalendarDayMeetings,
  useDraftMeetingTypes,
  useFiscalYears,
  useLocations
} from "@/services/draftCalendar";
import { useCanAddMeeting, useCanEditMeeting } from "@/hooks/usePermissions";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import type { CalendarDayMeeting, CalendarEvent, CalendarHoliday } from "@/types/draftCalendar";
import { MeetingFlyout } from "./MeetingFlyout";
import { CalendarEventSearch } from "./CalendarEventSearch";
import { ImportantDatesDialog } from "./ImportantDatesDialog";
import "../DraftCalendarPage.css";
import "./DraftCalendarView.css";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MAX_EVENTS_PER_DAY = 3;

type EventRangePart = "single" | "start" | "middle" | "end";

function parseCalendarDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parsed = parseISO(value.includes("T") ? value : `${value}T00:00:00`);
  return isValid(parsed) ? parsed : null;
}

function getDateKey(value: Date): string {
  return format(value, "yyyy-MM-dd");
}

function getMonthDays(monthDate: Date): Date[] {
  const firstVisible = startOfWeek(startOfMonth(monthDate));
  const lastVisible = endOfWeek(endOfMonth(monthDate));
  const days: Date[] = [];
  let cursor = firstVisible;

  while (cursor <= lastVisible) {
    days.push(cursor);
    cursor = addDays(cursor, 1);
  }

  return days;
}

function getEventDurationDays(event: CalendarEvent): number {
  const start = parseCalendarDate(event.startDate);
  const end = parseCalendarDate(event.endDate);
  if (!start || !end) return 1;

  const startTime = startOfDay(start).getTime();
  const endTime = startOfDay(end).getTime();
  return Math.max(1, Math.floor((endTime - startTime) / 86_400_000) + 1);
}

function compareCalendarEvents(left: CalendarEvent, right: CalendarEvent): number {
  const leftStart = parseCalendarDate(left.startDate)?.getTime() ?? 0;
  const rightStart = parseCalendarDate(right.startDate)?.getTime() ?? 0;
  if (leftStart !== rightStart) return leftStart - rightStart;

  const durationDifference = getEventDurationDays(right) - getEventDurationDays(left);
  if (durationDifference !== 0) return durationDifference;

  return (left.meetingName ?? "").localeCompare(right.meetingName ?? "");
}

function getEventsForDay(events: CalendarEvent[], day: Date): CalendarEvent[] {
  return events
    .filter((event) => {
      const start = parseCalendarDate(event.startDate);
      const end = parseCalendarDate(event.endDate);
      if (!start || !end) return false;

      return isWithinInterval(startOfDay(day), {
        start: startOfDay(start),
        end: endOfDay(end)
      });
    })
    .sort(compareCalendarEvents);
}

function sortCalendarEvents(events: CalendarEvent[]): CalendarEvent[] {
  return [...events].sort(compareCalendarEvents);
}

function getEventLanesForCalendar(events: CalendarEvent[], days: Date[]): Map<string, number> {
  const lanes = new Map<string, number>();
  const laneEvents: Array<CalendarEvent | null> = Array(MAX_EVENTS_PER_DAY).fill(null);
  const firstVisibleDay = days[0];

  days.forEach((day) => {
    laneEvents.forEach((event, index) => {
      const end = parseCalendarDate(event?.endDate);
      if (!event || !end || startOfDay(end) < startOfDay(day)) {
        laneEvents[index] = null;
      }
    });

    const startingEvents = sortCalendarEvents(events).filter((event) => {
      if (lanes.has(String(event.id))) return false;

      const start = parseCalendarDate(event.startDate);
      const end = parseCalendarDate(event.endDate);
      if (!start || !end) return false;

      const startsToday = isSameDay(start, day);
      const startedBeforeVisibleGrid = isSameDay(day, firstVisibleDay) && startOfDay(start) < startOfDay(day) && endOfDay(end) >= startOfDay(day);
      return startsToday || startedBeforeVisibleGrid;
    });

    startingEvents.forEach((event) => {
      const freeLane = laneEvents.findIndex((laneEvent) => laneEvent === null);
      if (freeLane === -1) return;

      laneEvents[freeLane] = event;
      lanes.set(String(event.id), freeLane);
    });
  });

  return lanes;
}

function getEventRangePart(event: CalendarEvent, day: Date): EventRangePart {
  const start = parseCalendarDate(event.startDate);
  const end = parseCalendarDate(event.endDate);
  if (!start || !end) return "single";

  const dayTime = startOfDay(day).getTime();
  const startTime = startOfDay(start).getTime();
  const endTime = startOfDay(end).getTime();

  if (startTime === endTime) return "single";
  if (dayTime === startTime) return "start";
  if (dayTime === endTime) return "end";
  return "middle";
}

/**
 * For a spanning event rendered as a single element within a week row,
 * determines the range part based on whether the event crosses the week boundary
 * rather than per-day position. This drives border-radius styling.
 */
function getSpanRangePart(event: CalendarEvent, week: Date[]): EventRangePart {
  const weekStart = week[0];
  const weekEnd = week[6];
  const eventStart = parseCalendarDate(event.startDate);
  const eventEnd = parseCalendarDate(event.endDate);
  if (!eventStart || !eventEnd) return "single";

  const startsBeforeWeek = startOfDay(eventStart) < startOfDay(weekStart);
  const endsAfterWeek = startOfDay(eventEnd) > startOfDay(weekEnd);

  if (startsBeforeWeek && endsAfterWeek) return "middle";
  if (startsBeforeWeek) return "end";
  if (endsAfterWeek) return "start";
  return "single";
}

interface WeekEventSlot {
  event: CalendarEvent;
  /** 1-indexed CSS grid-column start */
  colStart: number;
  /** Number of columns this event spans within the week */
  colSpan: number;
  /** First visible day of this event in the week (for text/label logic) */
  firstDay: Date;
}

/**
 * Returns the events assigned to a given lane that are visible in the specified week,
 * with their column position and span pre-calculated for CSS grid placement.
 */
function getWeekLaneSlots(
  events: CalendarEvent[],
  week: Date[],
  laneMap: Map<string, number>,
  laneIndex: number
): WeekEventSlot[] {
  const slots: WeekEventSlot[] = [];
  const weekStart = week[0];
  const weekEnd = week[6];

  for (const event of events) {
    if (laneMap.get(String(event.id)) !== laneIndex) continue;

    const eventStart = parseCalendarDate(event.startDate);
    const eventEnd = parseCalendarDate(event.endDate);
    if (!eventStart || !eventEnd) continue;

    // Skip events that don't overlap this week at all
    if (startOfDay(eventStart) > startOfDay(weekEnd) || startOfDay(eventEnd) < startOfDay(weekStart)) continue;

    // Clamp start column: if event started before this week, begin at column 0
    const rawStartIdx = startOfDay(eventStart) >= startOfDay(weekStart)
      ? week.findIndex((d) => isSameDay(d, eventStart))
      : 0;
    const colStart0 = rawStartIdx === -1 ? 0 : rawStartIdx;

    // Clamp end column: if event ends after this week, extend to column 6
    const rawEndIdx = startOfDay(eventEnd) <= startOfDay(weekEnd)
      ? week.findIndex((d) => isSameDay(d, eventEnd))
      : 6;
    const colEnd0 = rawEndIdx === -1 ? 6 : rawEndIdx;

    slots.push({
      event,
      colStart: colStart0 + 1, // CSS grid-column is 1-indexed
      colSpan: colEnd0 - colStart0 + 1,
      firstDay: week[colStart0],
    });
  }

  return slots;
}

function shouldShowEventText(event: CalendarEvent, day: Date, rangePart: EventRangePart, currentDate: Date): boolean {
  if (rangePart === "single" || rangePart === "start") return true;

  const start = parseCalendarDate(event.startDate);
  if (!start || startOfDay(start) >= startOfDay(day)) return false;

  const isWeekStartContinuation = day.getDay() === 0;
  const isCurrentMonthStartContinuation = isSameDay(day, startOfMonth(currentDate));
  return isWeekStartContinuation || isCurrentMonthStartContinuation;
}

function getEventRangeClass(rangePart: EventRangePart): string {
  switch (rangePart) {
    case "start":
      return "dcvEventRangeStart";
    case "middle":
      return "dcvEventRangeMiddle";
    case "end":
      return "dcvEventRangeEnd";
    case "single":
    default:
      return "dcvEventRangeSingle";
  }
}

function getHolidayClass(holiday: CalendarHoliday | undefined): string {
  switch (holiday?.calendarHolidayHeaderCss) {
    case "DoNotBlockHolidaysCalendarCss":
      return "dcvHolidayDoNotBlock";
    case "USHolidaysCalendarCss":
      return "dcvHolidayUs";
    case "DeloitteHolidaysCalendarCss":
      return "dcvHolidayDeloitte";
    case "ReligiousHolidaysCalendarCss":
      return "dcvHolidayReligious";
    default:
      return "";
  }
}

function formatPreferredDate(value: string | null | undefined): string {
  const parsed = parseCalendarDate(value);
  return parsed ? format(parsed, "EEE,MM-dd-yyyy") : "";
}

function formatEventDate(value: string | null | undefined): string {
  const parsed = parseCalendarDate(value);
  return parsed ? format(parsed, "MM/dd/yyyy") : "";
}

function PreferredDateIcon({ isPrimary }: { isPrimary: boolean }) {
  return (
    <CalendarCheck2
      aria-hidden="true"
      className={isPrimary ? "dcvDateIconPrimary" : "dcvDateIconSecondary"}
      size={16}
    />
  );
}

export interface DayMeetingsDialogProps {
  open: boolean;
  date: Date | null;
  meetings: CalendarDayMeeting[];
  isLoading: boolean;
  error: Error | null;
  onClose: () => void;
  onOpenMeeting: (id: number) => void;
}

export function DayMeetingsDialog({
  open,
  date,
  meetings,
  isLoading,
  error,
  onClose,
  onOpenMeeting
}: DayMeetingsDialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  /** Holds the element that had focus when the panel was opened so we can restore it. */
  const triggerElementRef = useRef<Element | null>(null);

  useEffect(() => {
    if (open) {
      // Capture the element that triggered the open before moving focus.
      triggerElementRef.current = document.activeElement;

      const firstFocusable = panelRef.current?.querySelector<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      // Use setTimeout to ensure the panel is rendered before focusing (handles
      // browsers that don't immediately reflect DOM updates in the same tick).
      setTimeout(() => firstFocusable?.focus(), 0);
    } else {
      // Restore focus to the trigger element when the panel closes.
      if (triggerElementRef.current instanceof HTMLElement) {
        triggerElementRef.current.focus();
      }
      triggerElementRef.current = null;
    }
  }, [open]);

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape") {
      onClose();
      return;
    }
    if (e.key !== "Tab") return;

    const focusables = Array.from(
      panelRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) ?? []
    );
    if (focusables.length === 0) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/30" onClick={onClose} aria-hidden="true" />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dcv-day-flyout-title"
        tabIndex={-1}
        className="fixed inset-y-0 right-0 z-50 flex h-full w-[30vw] min-w-[280px] flex-col border-l border-slate-300 bg-white shadow-xl"
        onKeyDown={handleKeyDown}
      >
        <div className="flex items-center justify-between bg-black px-4 py-3">
          <h2 id="dcv-day-flyout-title" className="text-base font-semibold text-white">
            {date ? `Meetings for ${format(date, "MMMM dd, yyyy")}` : "Meetings"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close meeting list"
            className="text-white hover:text-slate-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading && (
            <div className="dcvState" role="status">
              <Loader2 className="dcvSpin" size={18} />
              Loading meetings...
            </div>
          )}

          {error && !isLoading && (
            <div className="dcvAlert" role="alert">
              Unable to load meetings for this day.
            </div>
          )}

          {!isLoading && !error && meetings.length === 0 && (
            <div className="dcvState">No meetings found for this day.</div>
          )}

          {!isLoading && !error && meetings.length > 0 && (
            <ul className="dcvMeetingList" aria-label="Meetings for selected day">
              {meetings.map((meeting, index) => (
                <li key={meeting.id} className={index % 2 === 0 ? "dcvMeetingRowEven" : "dcvMeetingRowOdd"}>
                  <div className="dcvMeetingSummary">
                    <strong>{meeting.meetingName ?? "Untitled meeting"}</strong>
                    {meeting.preferredDatesDayResponse?.map((dateRange) => (
                      <div key={`${meeting.id}-${dateRange.id ?? dateRange.startDate}`} className="dcvDateRow">
                        <PreferredDateIcon isPrimary={dateRange.isItPrimaryDate} />
                        <span>
                          {formatPreferredDate(dateRange.startDate)} - {formatPreferredDate(dateRange.endDate)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="dcvOpenMeetingButton"
                    onClick={() => onOpenMeeting(meeting.id)}
                    aria-label={`Open ${meeting.meetingName ?? "meeting"}`}
                  >
                    <ChevronRight size={18} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}

export function DraftCalendarView() {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const userEmail = accounts[0]?.username ?? "";
  const currentUser = useCurrentUser();
  const canAddMeeting = useCanAddMeeting();
  const canEditMeeting = useCanEditMeeting();
  const monthLabelRef = useRef<HTMLButtonElement>(null);
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [meetingNameFilter, setMeetingNameFilter] = useState("");
  const [selectedFYId, setSelectedFYId] = useState(0);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [dayMeetings, setDayMeetings] = useState<CalendarDayMeeting[]>([]);
  const [flyoutMeetingId, setFlyoutMeetingId] = useState<number | null>(null);
  const [flyoutViewOnly, setFlyoutViewOnly] = useState(true);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  // Mirrors Blazor ShowRoomblockDateLegend — shown by default in calendar view, dismissible
  const [showRoomBlockBanner, setShowRoomBlockBanner] = useState(true);
  const [showHolidayDialog, setShowHolidayDialog] = useState(false);

  const month = currentDate.getMonth() + 1;
  const year = currentDate.getFullYear();
  const { data: events = [], isPending, isFetching, error, refetch } =
    useCalendarEvents(month, year, userEmail, meetingNameFilter);
  const { data: holidays = [], isFetching: isLoadingHolidays } = useCalendarHolidays(month, year);
  const dayMeetingsMutation = useDraftCalendarDayMeetings();

  const { data: fiscalYears = [], isPending: loadingFY } = useFiscalYears();
  const { data: businessesData } = useDraftBusinesses();
  const { data: meetingTypesMap = {} } = useDraftMeetingTypes();
  const { data: attendeeLevelsData } = useAttendeeLevels();
  const { data: locationsData } = useLocations();

  const defaultFiscalYear = useMemo(
    () => fiscalYears.find((fy) => fy.submissionStatus !== "Closed") ?? fiscalYears[0],
    [fiscalYears]
  );
  const activeFiscalYearId = selectedFYId || defaultFiscalYear?.id || 0;
  const activeFiscalYear = fiscalYears.find((fy) => fy.id === activeFiscalYearId) ?? defaultFiscalYear;
  const isFYClosed = activeFiscalYear?.submissionStatus === "Closed";
  const isAdmin = currentUser?.roleCode?.toLowerCase() === "admin";
  const isRestrictedRole = ["business_approver", "business_reviewer"].includes(currentUser?.roleCode?.toLowerCase() ?? "");
  const currentFYStart = parseCalendarDate(defaultFiscalYear?.startDate) ?? new Date();
  const currentFYEnd = parseCalendarDate(defaultFiscalYear?.endDate) ?? currentFYStart;
  const currentFYStartMonth = startOfMonth(currentFYStart);
  const currentFYEndMonth = startOfMonth(currentFYEnd);
  const { data: draftItemsForExport = [] } = useDraftCalendar(activeFiscalYearId, userEmail);

  useEffect(() => {
    setCurrentDate(currentFYStartMonth);
  }, [defaultFiscalYear?.id, defaultFiscalYear?.startDate]);

  const days = useMemo(() => getMonthDays(currentDate), [currentDate]);
  const weeks = useMemo<Date[][]>(() => {
    const result: Date[][] = [];
    for (let i = 0; i < days.length; i += 7) {
      result.push(days.slice(i, i + 7));
    }
    return result;
  }, [days]);
  const isOutsideCurrentFY = isBefore(currentDate, currentFYStartMonth) || isAfter(currentDate, currentFYEndMonth);
  const visibleEvents = isRestrictedRole && isOutsideCurrentFY ? [] : events;
  const visibleEventLanes = useMemo(
    () => getEventLanesForCalendar(visibleEvents, days),
    [days, visibleEvents]
  );
  const holidaysByDate = useMemo(() => {
    const map = new Map<string, CalendarHoliday>();
    holidays.forEach((holiday) => {
      const holidayDate = parseCalendarDate(holiday.date);
      if (holidayDate) map.set(getDateKey(holidayDate), holiday);
    });
    return map;
  }, [holidays]);

  function openMeeting(id: number) {
    setFlyoutMeetingId(id);
    setFlyoutViewOnly(true);
  }

  function openCreate() {
    setFlyoutMeetingId(0);
    setFlyoutViewOnly(false);
  }

  async function openDayMeetings(day: Date, dayEvents: CalendarEvent[]) {
    setSelectedDay(day);
    setDayMeetings([]);

    try {
      const meetings = await dayMeetingsMutation.mutateAsync(dayEvents.map((event) => event.id));
      setDayMeetings(meetings);
    } catch {
      // Mutation state drives the dialog error message.
    }
  }

  function handleDateChange(event: ChangeEvent<HTMLInputElement>) {
    const parsed = parseCalendarDate(event.target.value);
    if (parsed) setCurrentDateWithinAccess(parsed);
  }

  function setCurrentDateWithinAccess(date: Date) {
    setCurrentDate(date);
  }

  function handleFlyoutSaved() {
    setFlyoutMeetingId(null);
    void refetch();
  }

  const isLoading = isPending && isFetching;
  const filteredCount = visibleEvents.length;

  return (
    <main className="dcvPage draft-calendar-scroll-page" aria-label="Draft Calendar Schedule View">
      {/* Room-block date banner — mirrors Blazor @if (ShowRoomblockDateLegend) */}
      {showRoomBlockBanner && (
        <div className="roomblockDate-legend-banner">
          <span>Dates shown in this calendar represent room block periods, not meeting start/end dates.</span>
          <button
            type="button"
            className="banner-close"
            aria-label="Dismiss"
            onClick={() => setShowRoomBlockBanner(false)}
          >
            &#x2715;
          </button>
        </div>
      )}
      <div className="flex items-center border-b border-slate-200 bg-white px-3 py-2 gap-2">
        {/* Left: Month navigation */}
        <div className="flex flex-none items-center gap-2 shrink-0">
          <MonthNav
            label={format(currentDate, "MMMM yyyy")}
            onPrev={() => setCurrentDateWithinAccess(subMonths(currentDate, 1))}
            onNext={() => setCurrentDateWithinAccess(addMonths(currentDate, 1))}
            disablePrev={false}
            disableNext={false}
            monthLabelRef={monthLabelRef}
            onLabelClick={() => {
              if (monthLabelRef.current) {
                setTriggerRect(monthLabelRef.current.getBoundingClientRect());
              }
              setShowMonthPicker(true);
            }}
            showThisMonth
            onThisMonth={() => setCurrentDateWithinAccess(new Date())}
          />
          <div className="mx-1 h-6 w-px bg-slate-200" aria-hidden="true" />
          {(isLoading || isLoadingHolidays) && <Loader2 className="dcvSpin" size={18} aria-label="Loading calendar" />}
        </div>

        {/* Center: Search — CalendarEventSearch mirrors Blazor CalendarEventSearch */}
        <div className="flex flex-1 justify-center">
          <CalendarEventSearch
            userEmail={userEmail}
            value={meetingNameFilter}
            onValueChange={setMeetingNameFilter}
            onSelect={(item) => {
              if (!item) {
                setMeetingNameFilter("");
                return;
              }
              // Navigate to the meeting's start month (mirrors Blazor HandleValueChanged)
              if (item.eventStart) {
                try {
                  const start = new Date(item.eventStart);
                  if (!isNaN(start.getTime())) {
                    setCurrentDateWithinAccess(new Date(start.getFullYear(), start.getMonth(), 1));
                  }
                } catch { /* ignore */ }
              }
              setMeetingNameFilter(item.meetingName ?? "");
            }}
            placeholder="Search"
          />
        </div>

        {/* Right: Controls */}
        <div className="flex items-center gap-2 shrink-0">
          <div className="flex overflow-hidden border border-slate-300">
            <button
              type="button"
              onClick={() => navigate("/draftcalendar")}
              aria-label="List view"
              aria-pressed={false}
              className="flex h-8 w-8 items-center justify-center bg-white text-slate-600 hover:bg-slate-50"
            >
              <List className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label="Calendar view"
              aria-pressed={true}
              className="flex h-8 w-8 items-center justify-center border-l border-slate-300 bg-slate-900 text-white"
            >
              <CalendarDays className="h-4 w-4" />
            </button>
          </div>

          {(!currentUser || canAddMeeting) && !isFYClosed && (
            <button
              type="button"
              onClick={openCreate}
              className="Add-Request-button border border-slate-900 bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
            >
              Add Meeting
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4 border-b border-slate-100 bg-slate-50 px-4 py-1.5 text-xs text-slate-600">
        <span>{filteredCount} meeting{filteredCount !== 1 ? "s" : ""}</span>
        {isFetching && !isPending && (
          <span className="flex items-center gap-1 text-slate-400">
            <Loader2 className="h-3 w-3 animate-spin" /> Refreshing&hellip;
          </span>
        )}
        {isFYClosed && <span className="rounded bg-red-100 px-2 py-0.5 font-medium text-red-700">Fiscal Year Closed</span>}
      </div>

      {error && !isLoading && (
        <div className="dcvAlert" role="alert">
          Unable to load the calendar schedule.
        </div>
      )}

      <section className="dcvScheduler" aria-label="Monthly calendar">
        <div className="dcvWeekHeader" role="row">
          {WEEKDAYS.map((day) => (
            <div key={day} className="dcvWeekday" role="columnheader">
              {day}
            </div>
          ))}
        </div>

        <div className="dcvCalendarBody" role="grid" aria-label={format(currentDate, "MMMM yyyy")}>
          {weeks.map((week) => {
            const weekKey = getDateKey(week[0]);

            return (
              <div key={weekKey} className="dcvWeek">
                {/* Day number header row */}
                <div className="dcvDayHeaderRow">
                  {week.map((day) => {
                    const key = getDateKey(day);
                    const holiday = holidaysByDate.get(key);
                    const isOutsideMonth = !isSameMonth(day, currentDate);
                    const isToday = isSameDay(day, new Date());

                    return (
                      <div
                        key={key}
                        className={`dcvDayHeaderCell${isOutsideMonth ? " dcvDayOutside" : ""}`}
                        role="gridcell"
                        aria-label={format(day, "EEEE, MMMM d, yyyy")}
                      >
                        <span
                          className={`dcvDayNumber${isToday ? " dcvDayNumberToday" : ""} ${getHolidayClass(holiday)}`}
                          title={holiday?.description || undefined}
                          tabIndex={holiday?.description ? 0 : undefined}
                        >
                          {format(day, "d")}
                          {holiday?.description && (
                            <span className="dcvHolidayTooltip" role="tooltip">
                              {holiday.description.split(",").map((line) => (
                                <span key={line.trim()}>{line.trim()}</span>
                              ))}
                            </span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Event lanes — each lane is a 7-column grid; events span columns via grid-column */}
                {Array.from({ length: MAX_EVENTS_PER_DAY }, (_, laneIndex) => {
                    const slots = getWeekLaneSlots(visibleEvents, week, visibleEventLanes, laneIndex);

                  return (
                    <div key={laneIndex} className="dcvEventLane">
                      {slots.map((slot) => {
                        const rangePart = getSpanRangePart(slot.event, week);
                        const showText = shouldShowEventText(slot.event, slot.firstDay, rangePart, currentDate);
                        const startFmt = formatEventDate(slot.event.meetingStartDate ?? slot.event.startDate);
                        const endFmt = formatEventDate(slot.event.meetingEndDate ?? slot.event.endDate);
                        const dateRange = startFmt && endFmt ? ` ${startFmt}-${endFmt}` : "";
                        const eventLabel = `${slot.event.allInAttendees ?? 0} | ${slot.event.meetingName ?? "Untitled meeting"} | Meeting Dates: ${dateRange}`;

                        return (
                          <button
                            key={String(slot.event.id)}
                            type="button"
                            className={`dcvEventItem ${getEventRangeClass(rangePart)}${showText ? "" : " dcvEventContinuation"}`}
                            style={{ gridColumn: `${slot.colStart} / span ${slot.colSpan}` }}
                            onClick={() => openMeeting(slot.event.id)}
                            title={`${slot.event.meetingName ?? "Untitled meeting"}${dateRange}`}
                            aria-label={eventLabel}
                          >
                            <span className="dcvEventContent">
                              {showText ? eventLabel : "\u00a0"}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  );
                })}

                {/* +more buttons — one cell per day */}
                <div className="dcvWeekMoreRow">
                  {week.map((day) => {
                    const key = getDateKey(day);
                    const dayEvents = getEventsForDay(visibleEvents, day);
                    const hiddenEventCount =
                      dayEvents.length -
                      dayEvents.filter((e) => visibleEventLanes.has(String(e.id))).length;
                    const isOutsideMonth = !isSameMonth(day, currentDate);

                    return (
                      <div
                        key={key}
                        className={`dcvWeekMoreCell${isOutsideMonth ? " dcvDayOutside" : ""}`}
                      >
                        {hiddenEventCount > 0 && (
                          <button
                            type="button"
                            className="dcvMoreButton"
                            onClick={() => void openDayMeetings(day, dayEvents)}
                          >
                            +{hiddenEventCount} more
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <DayMeetingsDialog
        open={selectedDay !== null}
        date={selectedDay}
        meetings={dayMeetings}
        isLoading={dayMeetingsMutation.isPending}
        error={dayMeetingsMutation.error instanceof Error ? dayMeetingsMutation.error : null}
        onClose={() => {
          setSelectedDay(null);
          setDayMeetings([]);
        }}
        onOpenMeeting={(id) => {
          setSelectedDay(null);
          openMeeting(id);
        }}
      />

      {flyoutMeetingId !== null && (
        <MeetingFlyout
          meetingId={flyoutMeetingId}
          fiscalYear={activeFiscalYear}
          businesses={businessesData ?? {}}
          meetingTypes={meetingTypesMap}
          attendeeLevels={attendeeLevelsData ?? []}
          locations={locationsData ?? []}
          isViewOnly={flyoutViewOnly}
          lockedBy={null}
          canEdit={canEditMeeting}
          onEdit={() => setFlyoutViewOnly(false)}
          onSaved={handleFlyoutSaved}
          onClose={() => setFlyoutMeetingId(null)}
        />
      )}
      <MonthYearPicker
        isOpen={showMonthPicker}
        currentDate={currentDate}
        onDateSelect={setCurrentDateWithinAccess}
        onClose={() => setShowMonthPicker(false)}
        triggerRect={triggerRect}
      />

      {/* Footer — mirrors Blazor View Important Dates & Holidays link */}
      <div className="flex items-center justify-between border-t border-slate-100 bg-white px-4 py-1.5">
        <a
          href="#"
          onClick={(e) => { e.preventDefault(); setShowHolidayDialog(true); }}
          className="holiday-link"
        >
          View Important Dates &amp; Holidays
        </a>
        <span className="total-count">Total Count: {filteredCount}</span>
      </div>

      <ImportantDatesDialog open={showHolidayDialog} onClose={() => setShowHolidayDialog(false)} />
    </main>
  );
}